import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin, ConfigProvider } from 'antd';
import './App.css';
import './pages/client/client.scss';
import './components/modernTable/modernTable.scss';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './components/sidebar/Sidebar';
import Topbar from './components/topbar/Topbar';
import SuperviseurNavbar from './pages/superviseur/navbar/SuperviseurNavbar';

/* ─── Lazy-loaded pages ─── */
const Rightbar = lazy(() => import('./pages/rightbar/Rightbar'));
const Client = lazy(() => import('./pages/client/Client'));
const ClientRapport = lazy(() => import('./pages/client/clientRapport/ClientRapport'));
const ClientForm = lazy(() => import('./pages/client/form/ClientForm'));
const ClientEdit = lazy(() => import('./pages/client/form/formEdit/ClientEdit'));
const Sites = lazy(() => import('./pages/sites/Sites'));
const Traceur = lazy(() => import('./pages/traceur/Traceur'));
const TraceurRapport = lazy(() => import('./pages/traceur/traceurRapport/TraceurRapport'));
const TraceurForm = lazy(() => import('./pages/traceur/form/TraceurForm'));
const TraceurEdit = lazy(() => import('./pages/traceur/form/edit/TraceurEdit'));
const Operations = lazy(() => import('./pages/operations/Operations'));
const OperationRapport = lazy(() => import('./pages/operations/operationRapport/OperationRapport'));
const OperationGen = lazy(() => import('./pages/operations/form/OperationGen'));
const Affectations = lazy(() => import('./pages/affectations/Affectations'));
const AffectationForm = lazy(() => import('./pages/affectations/form/AffectationForm'));
const Numero = lazy(() => import('./pages/numero/Numero'));
const NumeroForm = lazy(() => import('./pages/numero/form/NumeroForm'));
const Vehicules = lazy(() => import('./pages/vehicules/Vehicules'));
const VehiculeRapport = lazy(() => import('./pages/vehicules/vehiculeRapport/VehiculeRapport'));
const VehiculesForm = lazy(() => import('./pages/vehicules/form/VehiculesForm'));
const Marques = lazy(() => import('./pages/vehicules/marques/Marques'));
// M&M (Task 61) : l'ancienne page « Nouvelle Marque » (/marque_form) est
// fusionnée dans la page unique Marques & Modèles ; l'URL historique redirige.
const MarqueForm = () => <Navigate to="/marques" replace />;
const Recharge = lazy(() => import('./pages/recharge/Recharge'));
const RechargeForm = lazy(() => import('./pages/recharge/form/Recharge_form'));
const RechargeOne = lazy(() => import('./pages/recharge/rechargeOne/RechargeOne'));
const Personnel = lazy(() => import('./pages/personnel/Personnel'));
const PersonnelForm = lazy(() => import('./pages/personnel/form/PersonnelForm'));
const PersonnelEdit = lazy(() => import('./pages/personnel/form/PersonnelEdit'));
const Paiement = lazy(() => import('./pages/paiement/Paiement'));
const Position = lazy(() => import('./pages/monitoring/position/Position'));
const Signale = lazy(() => import('./pages/monitoring/signale/Signale'));
const Facturation = lazy(() => import('./pages/facturation/Facturation'));
const FactureEffectue = lazy(() => import('./pages/facturation/factureEffectue/FactureEffectue'));
const Rapport = lazy(() => import('./pages/rapport/rapport'));
const RapportGlobal = lazy(() => import('./pages/rapport/rapportGlobal/RapportGlobal'));
const RapportClient = lazy(() => import('./pages/rapportGeneral/rapportClient/RapportClient'));
const RapportVehicule = lazy(() => import('./pages/rapportGeneral/RapportVehicule'));
const Dette = lazy(() => import('./pages/dette/Dette'));
const Depenses = lazy(() => import('./pages/depenses/Depenses'));
const PermissionTabs = lazy(() => import('./pages/permissions/components/PermissionTabs'));
const Parametres = lazy(() => import('./pages/parametres/Parametres'));
const Login = lazy(() => import('./pages/login/Login'));
const OAuthCallback = lazy(() => import('./pages/oauth/OAuthCallback'));
const PassWordForgot = lazy(() => import('./pages/passwordForgot/PassWordForgot'));
const Page404 = lazy(() => import('./pages/page404/Page404'));
const Superviseur = lazy(() => import('./pages/superviseur/Superviseur'));
const SuperviseurInstallation = lazy(() => import('./pages/superviseur/form/superviseurInstallation/SuperviseurInstallation'));
const SuperviseurControle = lazy(() => import('./pages/superviseur/form/superviseurControle/SuperviseurControle'));
const SuperviseurDement = lazy(() => import('./pages/superviseur/form/superviseurDemantelement/SuperviseurDement'));
const SuperviseurTransfert = lazy(() => import('./pages/superviseur/form/superviseurTransfert/SuperviseurTransfert'));
const SuperviseurRemplace = lazy(() => import('./pages/superviseur/form/superviseurRemplacement/SuperviseurRemplace'));
const UserProfile = lazy(() => import('./pages/profile/UserProfile'));
const RapportFinance = lazy(() => import('./pages/finance/RapportFinance'));
const Presence = lazy(() => import('./pages/presence/Presence'));
const JoursFeries = lazy(() => import('./pages/presence/joursFeries/JoursFeries'));
const Horaires = lazy(() => import('./pages/presence/horaires/Horaires'));
const SalairePage = lazy(() => import('./pages/presence/salaire/Salaire'));
const Terminaux = lazy(() => import('./pages/presence/terminaux/Terminaux'));
const Calendrier = lazy(() => import('./pages/presence/calendrier/Calendrier'));
const DashboardRH = lazy(() => import('./pages/presence/dashboard/DashboardRH'));

/* ─── Loading fallback ─── */
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

function App() {
  const user = useSelector((state) => state.user?.currentUser);

  const SecuriteRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/login" />;
    }
    return children;
  };

  const Layout = () => (
    <div className='app_wrapper'>
      <div className="appContainer">
        <Sidebar />
        <div className="appOutlet">
          <Topbar />
          <div className="outlet-wrapper">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );

  const Layout2 = () => (
    <div>
      <SuperviseurNavbar />
      <div className="pageNavbar">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );

  const adminOrSecretaireRoutes = (user?.role === 'admin' || user?.role === 'secretaire') ? [
    {
      path: '/',
      element: <SecuriteRoute><Layout /></SecuriteRoute>,
      children: [
        { path: '/', element: <Rightbar /> },
        { path: '/client', element: <Client /> },
        { path: '/clientRapport', element: <ClientRapport /> },
        { path: '/client_form', element: <ClientForm /> },
        { path: '/clientEdit', element: <ClientEdit /> },
        { path: '/sites', element: <Sites /> },
        { path: '/traceurs', element: <Traceur /> },
        { path: '/traceurRapport', element: <TraceurRapport /> },
        { path: '/traceurs_form', element: <TraceurForm /> },
        { path: '/traceurEdit', element: <TraceurEdit /> },
        { path: '/operations', element: <Operations /> },
        { path: '/operationRapport', element: <OperationRapport /> },
        { path: '/operations_form', element: <OperationGen /> },
        { path: '/affectation', element: <Affectations /> },
        { path: '/affectation_form', element: <AffectationForm /> },
        { path: '/numero', element: <Numero /> },
        { path: '/numero_form', element: <NumeroForm /> },
        { path: '/vehicules', element: <Vehicules /> },
        { path: '/vehiculeRapport', element: <VehiculeRapport /> },
        { path: '/vehicule_form', element: <VehiculesForm /> },
        { path: '/marques', element: <Marques /> },
        { path: '/marque_form', element: <MarqueForm /> },
        { path: '/recharge', element: <Recharge /> },
        { path: '/recharge_form', element: <RechargeForm /> },
        { path: '/rechargeOne', element: <RechargeOne /> },
        { path: '/personnel', element: <Personnel /> },
        { path: '/personnel_form', element: <PersonnelForm /> },
        { path: '/personnel_edit', element: <PersonnelEdit /> },
        { path: '/paiement', element: <Paiement /> },
        { path: '/position', element: <Position /> },
        { path: '/signal', element: <Signale /> },
        { path: '/facturation', element: <Facturation /> },
        { path: '/factureEffectue', element: <FactureEffectue /> },
        { path: '/facturation_form', element: <FactureEffectue /> },
        { path: '/rapport', element: <Rapport /> },
        { path: '/rapport_global', element: <RapportGlobal /> },
        { path: '/rapport_client', element: <RapportClient /> },
        { path: '/rapport_vehicule', element: <RapportVehicule /> },
        { path: '/dette', element: <Dette /> },
        { path: '/depense', element: <Depenses /> },
        { path: '/permissions', element: <PermissionTabs /> },
        { path: '/parametres', element: <Parametres /> },
        { path: '/profile', element: <UserProfile /> },
        { path: '/rapport_finance', element: <RapportFinance /> },
        { path: '/presence', element: <Presence /> },
        { path: '/presence/jours_feries', element: <JoursFeries /> },
        { path: '/presence/horaires', element: <Horaires /> },
        { path: '/presence/salaire', element: <SalairePage /> },
        { path: '/presence/calendrier', element: <Calendrier /> },
        { path: '/presence/dashboard', element: <DashboardRH /> },
        { path: '/presence/terminaux', element: <Terminaux /> }
      ]
    }
  ] : [];

  const superviseurRoutes = (user?.role === 'superviseur') ? [
    {
      path: '/',
      element: <SecuriteRoute><Layout2 /></SecuriteRoute>,
      children: [
        { path: '/', element: <Superviseur /> },
        { path: '/installation', element: <SuperviseurInstallation /> },
        { path: '/controle_technique', element: <SuperviseurControle /> },
        { path: '/demantelement', element: <SuperviseurDement /> },
        { path: '/transfert', element: <SuperviseurTransfert /> },
        { path: '/remplacement', element: <SuperviseurRemplace /> },
        { path: '/profile', element: <UserProfile /> },
      ]
    }
  ] : [];

  const routes = [
    ...adminOrSecretaireRoutes,
    ...superviseurRoutes,
    { path: '/login', element: <Suspense fallback={<PageLoader />}><Login /></Suspense> },
    { path: '/oauth-callback', element: <Suspense fallback={<PageLoader />}><OAuthCallback /></Suspense> },
    { path: '/forgot', element: <Suspense fallback={<PageLoader />}><PassWordForgot /></Suspense> },
    { path: '/*', element: <SecuriteRoute><Suspense fallback={<PageLoader />}><Page404 /></Suspense></SecuriteRoute> },
  ];

  const router = createBrowserRouter(routes);

  return (
    <ConfigProvider theme={{
        token: {
          colorPrimary: '#13AED8',
          borderRadius: 5,
        },
      }}>
      <div className="App">
        <ToastContainer />
        <div className="app-container">
          <RouterProvider router={router} />
        </div>
      </div>
    </ConfigProvider>
  );
}

export default App;